import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { PlacedWidget } from './types';
import { getWidgetComponent, UnknownWidget } from './registry';

interface WidgetRendererProps {
  widget: PlacedWidget;
  isEditing: boolean;
  onRemove?: () => void;
}

/**
 * Error boundary scoped to ONE widget.
 *
 * Without this a single widget throwing during render unmounts the entire
 * grid — one bad response and the whole dashboard is a white screen. Class
 * component because React still has no hook equivalent for
 * componentDidCatch.
 */
class WidgetErrorBoundary extends React.Component<
  { children: React.ReactNode; widgetKey: string },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error) {
    // eslint-disable-next-line no-console
    console.error(`Widget "${this.props.widgetKey}" crashed:`, error);
  }

  render() {
    if (this.state.error) {
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            textAlign: 'center',
            color: 'text.secondary',
            fontSize: '0.875rem',
          }}
        >
          This widget failed to render.
        </Box>
      );
    }
    return this.props.children as React.ReactElement;
  }
}

const WidgetRenderer: React.FC<WidgetRendererProps> = ({ widget, isEditing }) => {
  const Component = getWidgetComponent(widget.widgetKey);

  // The server strips widgets whose key it no longer knows, but a client
  // build can also lag behind the registry. Degrade to a tile, never crash.
  if (!Component || !widget.descriptor) {
    return <UnknownWidget widgetKey={widget.widgetKey} />;
  }

  return (
    <WidgetErrorBoundary widgetKey={widget.widgetKey}>
      <React.Suspense
        fallback={
          <Box sx={{ p: 2, height: '100%' }}>
            <Skeleton variant="text" width="50%" />
            <Skeleton variant="rectangular" height="70%" sx={{ mt: 1, borderRadius: 1 }} />
          </Box>
        }
      >
        <Component
          config={(widget.config as Record<string, unknown>) || {}}
          descriptor={widget.descriptor}
          isEditing={isEditing}
        />
      </React.Suspense>
    </WidgetErrorBoundary>
  );
};

export default WidgetRenderer;
